/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Vector Search Operations
 * 
 * Handles MCP Vector Search server operations for semantic code search.
 */

import { authenticatedFetch } from '@shared/lib/api';
import { handleApiResponse } from '@shared/lib/apiResponse';
import { getRepoIdentifier } from '../../utils/repoPaths';

export interface VectorSearchRepo {
  repoId: string;
  displayName: string;
  repoPath: string;
  isIndexed: boolean;
  lastIndexed?: string;
  chunkCount: number;
  fileCount: number;
  languages: string[];
}

/**
 * Get the status of the MCP Vector Search server
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function getVectorSearchStatus(): Promise<{
  connected: boolean;
  serverId: string;
  tools: string[];
  toolCount: number;
  error?: string;
}> {
  try {
    const response = await authenticatedFetch('/api/vector-search/status');
    const result = await handleApiResponse<{
      connected: boolean;
      serverId: string;
      tools: string[];
      toolCount: number;
    }>(response);

    return result;
  } catch (error: any) {
    console.error('[SkillShop Service] Error getting vector search status:', error);
    return {
      connected: false,
      serverId: 'mcp-vector-search',
      tools: [],
      toolCount: 0,
      error: error.message || 'Failed to get vector search status'
    };
  }
}

/**
 * List all repositories registered with MCP Vector Search
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function listVectorSearchRepos(): Promise<{
  repos: VectorSearchRepo[];
  totalCount: number;
}> {
  try {
    const response = await authenticatedFetch('/api/vector-search/repos');
    return await handleApiResponse(response);
  } catch (error: any) {
    console.error('[SkillShop Service] Error listing vector search repos:', error);
    return { repos: [], totalCount: 0 };
  }
}

/**
 * Register a repository for vector search indexing
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function registerVectorSearchRepo(repoPath: string, displayName: string, repoId?: string): Promise<{
  success: boolean;
  repoId: string;
  displayName: string;
  collectionName: string;
  message: string;
  error?: string;
}> {
  try {
    console.log('[SkillShop Service] Registering vector search repo:', { repoPath, displayName, repoId });

    const response = await authenticatedFetch('/api/vector-search/repos', {
      method: 'POST',
      body: {
        repoPath,
        displayName,
        repoId
      }
    });

    const result = await handleApiResponse<{
      success: boolean;
      repoId: string;
      displayName: string;
      collectionName: string;
      message: string;
    }>(response);

    console.log('[SkillShop Service] Vector search repo registered:', result);
    return result;
  } catch (error: any) {
    console.error('[SkillShop Service] Error registering vector search repo:', error);
    return {
      success: false,
      repoId: repoId || '',
      displayName,
      collectionName: '',
      message: 'Failed to register repository',
      error: error.message || 'Unknown error'
    };
  }
}

/**
 * Get the status of a specific repository in vector search
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function getVectorSearchRepoStatus(repoId: string): Promise<VectorSearchRepo | null> {
  try {
    const response = await authenticatedFetch(`/api/vector-search/repos/${encodeURIComponent(repoId)}`);
    return await handleApiResponse(response);
  } catch (error: any) {
    console.error('[SkillShop Service] Error getting vector search repo status:', error);
    return null;
  }
}

/**
 * Index or re-index a repository for vector search
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function indexVectorSearchRepo(repoId: string, force: boolean = false): Promise<{
  success: boolean;
  repoId: string;
  indexedFiles: number;
  indexedChunks: number;
  duration?: number;
  message: string;
  error?: string;
}> {
  try {
    console.log('[SkillShop Service] Indexing vector search repo:', { repoId, force });

    const response = await authenticatedFetch(`/api/vector-search/repos/${encodeURIComponent(repoId)}/index`, {
      method: 'POST',
      body: { force }
    });

    const result = await handleApiResponse<{
      success: boolean;
      repoId: string;
      indexedFiles: number;
      indexedChunks: number;
      duration?: number;
      message: string;
    }>(response);

    console.log('[SkillShop Service] Vector search repo indexed:', result);
    return result;
  } catch (error: any) {
    console.error('[SkillShop Service] Error indexing vector search repo:', error);
    return {
      success: false,
      repoId,
      indexedFiles: 0,
      indexedChunks: 0,
      message: 'Failed to index repository',
      error: error.message || 'Unknown error'
    };
  }
}

/**
 * Ensure a repository is registered and indexed for vector search
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function ensureVectorSearchRepoIndexed(repoPath: string, displayName: string, forceReindex: boolean = false): Promise<{
  success: boolean;
  repoId: string | null;
  wasIndexed: boolean;
  message: string;
  /** True when indexing was started in the background (not yet complete) */
  indexingStarted?: boolean;
  error?: string;
}> {
  try {
    console.log('[SkillShop Service] Ensuring vector search repo is indexed:', { repoPath, displayName, forceReindex });

    const response = await authenticatedFetch('/api/vector-search/ensure-indexed', {
      method: 'POST',
      body: {
        repoPath,
        displayName,
        forceReindex
      }
    });

    const result = await handleApiResponse<{
      success: boolean;
      repoId: string | null;
      wasIndexed: boolean;
      message: string;
      indexingStarted?: boolean;
    }>(response);

    console.log('[SkillShop Service] Vector search repo ensured indexed:', result);
    return result;
  } catch (error: any) {
    console.error('[SkillShop Service] Error ensuring vector search repo is indexed:', error);
    return {
      success: false,
      repoId: null,
      wasIndexed: false,
      message: 'Failed to ensure repository is indexed',
      error: error.message || 'Unknown error'
    };
  }
}

/**
 * Notify MCP Vector Search server about a product change
 * Registers and indexes the repository if needed.
 */
export async function notifyVectorSearchProductChanged(product: {
  title: string;
  repositoryUrl: string;
  repoPath?: string | null;
}): Promise<{ success: boolean; repoId?: string; error?: string }> {
  try {
    if (!product.repositoryUrl) {
      console.log('[SkillShop Service] Product has no repository URL, skipping vector search notification');
      return { success: true };
    }

    const repoIdentifier = getRepoIdentifier(product.repositoryUrl);
    const repoPathSuffix = product.repoPath || '';
    
    const velocityPath = `~/.velocity/repos/${repoIdentifier}/velocity${repoPathSuffix ? '/' + repoPathSuffix : ''}`;

    console.log('[SkillShop Service] Notifying vector search of product change:', {
      productTitle: product.title,
      repoUrl: product.repositoryUrl,
      repoPath: product.repoPath,
      velocityPath
    });

    const result = await ensureVectorSearchRepoIndexed(
      velocityPath,
      product.title,
      false
    );

    if (result.success) {
      console.log('[SkillShop Service] Vector search product notification successful:', result.repoId);
      return { success: true, repoId: result.repoId || undefined };
    } else {
      console.warn('[SkillShop Service] Vector search product notification failed:', result.error);
      return { success: false, error: result.error };
    }
  } catch (error: any) {
    console.warn('[SkillShop Service] Failed to notify vector search of product change:', error);
    return {
      success: false,
      error: error.message || 'Failed to notify vector search'
    };
  }
}

